import type { Node, Edge } from '@xyflow/react';

/**
 * 캔버스 선택 영역 복사/붙여넣기 — Ctrl+C / Ctrl+V / Ctrl+D (WorkflowCanvas 가 사용).
 * 클립보드는 모듈 변수라 워크플로를 오가며 붙여넣을 수 있다(다른 워크플로에도 복제 가능).
 */
let clipboard: { nodes: Node[]; edges: Edge[] } | null = null;
const PASTE_OFFSET = 40;

const deep = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

/** 선택된 노드(그룹이면 자식까지)와 그 사이의 엣지를 복사 → 담긴 노드 수 */
export function copySelection(nodes: Node[], edges: Edge[]): number {
  const ids = new Set(nodes.filter((n: any) => n.selected).map((n) => n.id));
  if (!ids.size) { return 0; }
  // 그룹을 골랐으면 자식(그 자식까지)도 따라온다
  let grew = true;
  while (grew) {
    grew = false;
    nodes.forEach((n: any) => {
      if (n.parentId && ids.has(n.parentId) && !ids.has(n.id)) { ids.add(n.id); grew = true; }
    });
  }
  clipboard = {
    nodes: nodes.filter((n) => ids.has(n.id)).map(deep),
    edges: edges.filter((e) => ids.has(e.source) && ids.has(e.target)).map(deep),
  };
  return clipboard.nodes.length;
}

export function hasClipboard(): boolean {
  return !!clipboard && clipboard.nodes.length > 0;
}

/**
 * 붙여넣을 복제본 — 모든 id 를 새로 매기고, 최상위 노드는 40px 어긋난 자리에 둔다.
 * 그룹째 복제하면 자식은 상대 위치를 유지하고, 자식만 복제하면 절대 좌표로 풀어낸다.
 * 연속 붙여넣기가 겹치지 않도록 클립보드 원본 좌표도 한 칸씩 민다.
 */
export function cloneForPaste(allNodes: Node[]): { nodes: Node[]; edges: Edge[] } | null {
  if (!clipboard || !clipboard.nodes.length) { return null; }
  const stamp = Date.now().toString(36);
  const inClip = new Set(clipboard.nodes.map((n) => n.id));
  const idMap: Record<string, string> = {};
  clipboard.nodes.forEach((n, i) => { idMap[n.id] = `${n.id}_c${stamp}${i.toString(36)}`; });

  const nodes = clipboard.nodes.map((src: any) => {
    const n: any = deep(src);
    n.id = idMap[src.id];
    n.selected = true;
    delete n.dragging; delete n.measured;
    if (src.parentId && inClip.has(src.parentId)) {
      n.parentId = idMap[src.parentId];               // 그룹째 — 상대 위치 유지
    } else {
      if (src.parentId) {                             // 부모 없이 자식만 — 절대 좌표로
        const parent: any = allNodes.find((p) => p.id === src.parentId);
        if (parent) { n.position = { x: parent.position.x + src.position.x, y: parent.position.y + src.position.y }; }
        delete n.parentId; delete n.extent;
      }
      n.position = { x: n.position.x + PASTE_OFFSET, y: n.position.y + PASTE_OFFSET };
    }
    return n as Node;
  });
  const edges = clipboard.edges.map((src: any, i: number) => {
    const e: any = deep(src);
    e.id = `${src.id}_c${stamp}${i.toString(36)}`;
    e.source = idMap[src.source];
    e.target = idMap[src.target];
    e.selected = false;
    return e as Edge;
  });

  // 다음 붙여넣기는 한 칸 더 아래·오른쪽에
  clipboard.nodes.forEach((n: any) => {
    if (!(n.parentId && inClip.has(n.parentId))) {
      n.position = { x: n.position.x + PASTE_OFFSET, y: n.position.y + PASTE_OFFSET };
    }
  });
  return { nodes, edges };
}
